import { useCallback, useState, useEffect } from 'react'
import { PaginatedFormWrapper } from '../PaginatedFormWrapper'
import { filterMaybe, formatMakeModelYear, isMac } from '@edge/spark/utils'
import {
    EditSaleConditionsUpdateQueryVariables,
    useEditSaleConditionsDetailQuery,
    EditSaleConditionsUpdateQuery,
    EditSaleConditionsUpdateDocument,
} from '../generated/graphql'
import { EditSaleConditionsFormFields } from './EditSaleConditionsFormFields'
import { useTableContext } from '@edge/spark/io-react-table'
import { createDialog } from '@edge/spark/feature-dialog'
import { useApolloClient } from '@apollo/react-hooks'
import { closeActionDrawer } from '../action-drawer.store'
import { zodResolver } from '@hookform/resolvers/zod'
import { useActiveOrganizationType } from '@edge/spark/io-roles'
import * as z from 'zod'
import { lightOptionsNormalizer } from '../lightOptionsNormalizer'
import { Redirect } from 'react-router-dom'
import { useIsAuthorized } from './useIsAuthorized'
import { FormProvider, useForm } from 'react-hook-form'
import { Box } from '@mui/material'
import { ProxyBidsCounter } from '@edge/spark/ui-mdi-icons'
import CancelProxyDialog from './CancelProxyDialog'
import formatLaneLot from 'src/utils/formatLaneLot'

const schema = z.object({
    amt: z.coerce.number().min(0, { message: 'Must be greater than or equal to 0' }).max(999_999),
    ments: z.array(z.string().optional()).optional(),
    ight: z.string(),
})

type FormValues = {
    amt: number
    ments: string[]
    ight: string
}

const useHandleUpdateCompleted = () => {
    const setRowState = useTableContext((s) => s.setRowState)
    return ({ stockNumber, data }: { stockNumber: string; data: EditSaleConditionsUpdateQuery }) => {
        try {
            const results = data.auction?.setVehicleInfo?.results
            const cancelProxyBidsResults = data.auction?.setVehicleInfo?.cancelProxyBidsResult

            if (!results?.length) {
                throw new Error(`Error updating #${stockNumber}. Missing Results`)
            }

            const errorResults = results.filter((r) => r?.success !== true)

            if (errorResults.length) {
                const errors = errorResults.map((r) => r?.operation).join(', ')

                throw new Error(`Error updating #${stockNumber}: ${errors}`)
            }

            if (cancelProxyBidsResults && cancelProxyBidsResults?.success === false) {
                const errorsProxyBidCancelation = cancelProxyBidsResults?.errors?.join(', ') ?? ''
                throw new Error(
                    `Please check again. Error cancelling proxy bids for #${stockNumber} ${errorsProxyBidCancelation}`,
                )
            }

            setRowState(stockNumber, { status: 'success' })

            createDialog({
                message: `Update #${stockNumber} successful`,
                action: 'OK',
                timeout: 5_000,
            })
        } catch (error) {
            console.error(error)

            const errorMessage = error instanceof Error ? error.message : 'Unknown error'
            setRowState(stockNumber, { status: 'error', message: errorMessage })

            return createDialog({
                message: errorMessage,
                action: 'OK',
                timeout: 15_000,
            })
        }
    }
}

export const EditSaleConditionsActionDrawer = () => {
    const setRowState = useTableContext((s) => s.setRowState)
    const incrementActiveRowByIndex = useTableContext((s) => s.incrementActiveRowByIndex)
    const decrementActiveRowByIndex = useTableContext((s) => s.decrementActiveRowByIndex)
    const clearActiveRowKey = useTableContext((s) => s.clearActiveRowKey)
    const activeStockNumber = useTableContext((s) => s.activeRowKey)
    const activeItemFromState = useTableContext((s) => s.getActiveRow())
    const setFirstSelectedAsActiveRow = useTableContext((s) => s.setFirstSelectedAsActiveRow)
    const handleUpdateCompleted = useHandleUpdateCompleted()
    const apolloClient = useApolloClient()
    const isAuthed = useIsAuthorized()
    const foundRole = isAuthed
    const [showCancelProxyBidsModal, setShowCancelProxyBidsModal] = useState<boolean>(false)
    const [submitterButton, setSubmitterButton] = useState<string>('')

    // Set first selected row as active row on first render
    useEffect(() => {
        setFirstSelectedAsActiveRow()
    }, [])

    const { data: queryData, loading: queryLoading } = useEditSaleConditionsDetailQuery({
        variables: { stockNumber: activeStockNumber },
        skip: !activeStockNumber || !foundRole,
        onCompleted: useCallback(() => {
            formMethods.setFocus('amt')
        }, []),
    })

    const activeItemFromData = queryData?.auction?.assets?.byId
    const activeItem = (activeItemFromData || activeItemFromState) as unknown as typeof activeItemFromData
    const defaultValues = {
        amt: activeItem?.floorAmount || 0,
        ments:
            activeItem?.announcements
                ?.filter((a) => a?.ordinal)
                .map((a) => a?.announcement)
                .filter((a): a is string => typeof a === 'string') || [],
        ight: activeItem?.listingConditionCode || '',
    }

    useEffect(() => {
        if (activeItem) {
            formMethods.reset(defaultValues)
        }
    }, [activeItem?.stockNumber])

    const formMethods = useForm<FormValues>({ resolver: zodResolver(schema), values: defaultValues })

    const auctionType = useActiveOrganizationType()

    const handleKeyPress = useCallback(
        (event: KeyboardEvent) => {
            /**
             * Meta/Ctrl + Right Arrow Keyboard Shortcut (Overrides browser forward page navigation on macOS)
             * ⌘ + → (macOS) or ^ + → (other OS)
             * Used for Next Vehicle
             */
            if (
                ((isMac && !event.ctrlKey && event.metaKey) || (!isMac && event.ctrlKey && !event.metaKey)) &&
                !event.altKey &&
                !event.shiftKey &&
                event.key === 'ArrowRight'
            ) {
                event.preventDefault() // prevent browser from going forward
                incrementActiveRowByIndex()
            }

            /**
             * Meta/Ctrl + Left Arrow Keyboard Shortcut (Overrides browser forward page navigation on macOS)
             * ⌘ + ← (macOS) or ^ + ← (other OS)
             * Used for Previous Vehicle
             */
            if (
                ((isMac && !event.ctrlKey && event.metaKey) || (!isMac && event.ctrlKey && !event.metaKey)) &&
                !event.altKey &&
                !event.shiftKey &&
                event.key === 'ArrowLeft'
            ) {
                event.preventDefault() // prevent browser from going back
                decrementActiveRowByIndex()
            }
        },
        [incrementActiveRowByIndex, decrementActiveRowByIndex],
    )

    /** Listen for all keyboard events */
    useEffect(() => {
        document.addEventListener('keydown', handleKeyPress)

        return () => {
            document.removeEventListener('keydown', handleKeyPress)
        }
    }, [handleKeyPress])

    const handleClose = () => {
        clearActiveRowKey()
        closeActionDrawer()
    }

    if (!foundRole) {
        console.error('You do not have permission to perform this action')
        createDialog({
            message: 'You do not have permission to perform this action',
            action: 'OK',
            timeout: 15_000,
        })
        return <Redirect to="/" />
    }

    if (!activeStockNumber) {
        return <h1>No stockNumber</h1>
    }

    const lights = queryData?.auction?.lightOptions?.filter(filterMaybe) || []
    const normalizedLightOptions = lightOptionsNormalizer(lights, { type: auctionType })

    if (!queryData && !queryLoading) {
        createDialog({
            message: `Failed to get data for ${activeStockNumber}`,
        })
    }

    if (!activeItem || !activeStockNumber) return <h1>No selected Item</h1>

    /**
     * The EditSaleConditionsUpdate uses a auction/setVehicleInfo query endpoint rather than a mutation.
     * Mutation hooks return promises, but Query hooks do not.
     *
     * Thus, this component uses the apollo client directly rather than a react hook
     * to call the query as a promise.
     * */
    const sendRequest = async (values: FormValues, cancelProxyBids = false) => {
        if (!activeItem.stockNumber) {
            throw new Error('Missing stock number')
        }

        const vars: EditSaleConditionsUpdateQueryVariables = {
            amsType: auctionType,
            assetId: activeItem.assetId,
            floorPrice: { oldValue: activeItem.floorAmount, newValue: values.amt },
            lights: { oldValue: activeItem.listingConditionCode, newValue: values.ight },
            announcements: values.ments.map((a, i) => ({
                ordinal: i + 1,
                announcement: a || '',
            })),
            stockNumber: activeItem.stockNumber,
        }
        if (cancelProxyBids) {
            vars.cancelProxyBids = cancelProxyBids
        }

        const res = await apolloClient.query<EditSaleConditionsUpdateQuery>({
            query: EditSaleConditionsUpdateDocument,
            variables: {
                ...vars,
            },
        })

        return res
    }

    const onSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
        event.preventDefault()
        event.stopPropagation()

        if (activeItem.proxyBidCount && activeItem.proxyBidCount > 0) {
            setSubmitterButton((event?.nativeEvent as SubmitEvent)?.submitter?.getAttribute('name') || '')
            setShowCancelProxyBidsModal(true)
            return
        }

        return formMethods.handleSubmit(async (values) => {
            setRowState(activeStockNumber, { status: 'loading' })

            if (!activeItem.stockNumber) {
                throw new Error('Missing stock number')
            }

            handlePageNavigation(event)

            const res = await sendRequest(values)
            return handleUpdateCompleted({ stockNumber: activeStockNumber, data: res.data })
        })(event)
    }

    /**
     * Function of side effect to handle page navigation while the request is being made
     */
    const handlePageNavigation = (event: React.FormEvent) => {
        const submitterName = (event?.nativeEvent as SubmitEvent)?.submitter?.getAttribute('name') || submitterButton

        if (!submitterName) {
            console.error('Invalid event object', event)
        }

        if (submitterName === 'save-and-next') {
            incrementActiveRowByIndex()
        }

        if (submitterName === 'save-and-done') {
            handleClose()
        }
    }

    const handleUpdateAndKeepProxyBids: React.FormEventHandler<HTMLFormElement> = async (event) => {
        setShowCancelProxyBidsModal(false)
        handlePageNavigation(event)
        const values = formMethods.getValues()
        const res = await sendRequest(values, false)
        return handleUpdateCompleted({ stockNumber: activeStockNumber, data: res.data })
    }

    const handleUpdateAndCancelProxyBids: React.FormEventHandler<HTMLFormElement> = async (event) => {
        handlePageNavigation(event)
        setShowCancelProxyBidsModal(false)
        const values = formMethods.getValues()
        const res = await sendRequest(values, true)
        return handleUpdateCompleted({ stockNumber: activeStockNumber, data: res.data })
    }

    const handleDiscard = () => {
        setShowCancelProxyBidsModal(false)
    }

    const modalMessage = `${formatMakeModelYear(activeItem)}  Stock# ${activeItem.stockNumber}`
    const runNumber = formatLaneLot({ lane: activeItem.saleListing?.lane, lot: activeItem.saleListing?.lot })

    return (
        <FormProvider {...formMethods}>
            <Box component="form" noValidate sx={{ display: 'flex', flex: 1, p: 2 }} onSubmit={onSubmit}>
                <PaginatedFormWrapper
                    loading={queryLoading}
                    workflowTitle="Edit Sale Conditions"
                    title={formatMakeModelYear(activeItem)}
                    extraIcons={<ProxyBidsCounter count={activeItem?.proxyBidCount} />}
                    subtitle={activeItem.consignor?.displayName || ''}
                    descriptionList={[
                        { term: 'VIN', detail: activeItem.vin || '' },
                        { term: 'Stock', detail: activeItem.stockNumber || '' },
                        { term: 'Run', detail: runNumber || '' },
                    ]}
                    onClose={handleClose}
                >
                    <EditSaleConditionsFormFields
                        lightOptions={normalizedLightOptions}
                        loading={queryLoading}
                        auctionType={auctionType}
                    />
                </PaginatedFormWrapper>
            </Box>
            <CancelProxyDialog
                title={'Save Changes and Cancel Proxy Bids?'}
                open={showCancelProxyBidsModal}
                onCancel={handleDiscard}
                postMessageEm={modalMessage}
                proxyBidCount={activeItem.proxyBidCount}
                onSaveAndKeepBids={handleUpdateAndKeepProxyBids}
                onSaveAndCancelBids={handleUpdateAndCancelProxyBids}
            />
        </FormProvider>
    )
}
